/** Team Handoff CTA clusters scoped per communication channel. */
export const HANDOFF_CTA_CHANNELS = [
  "chat",
  "phone",
  "sms",
  "email",
  "talk",
] as const;

export type HandoffCtaChannel = (typeof HANDOFF_CTA_CHANNELS)[number];

export type CtaClusterRecord = {
  id?: string;
  ctaQuestion: string;
  qualificationQuestions: string[];
  locationRouting?: string;
  channel?: string | null;
};

const LEGACY_CHAT_CHANNEL = "chat";

function normalizeChannel(raw: string | null | undefined): HandoffCtaChannel | null {
  if (!raw) return null;
  const value = raw.trim().toLowerCase();
  return (HANDOFF_CTA_CHANNELS as readonly string[]).includes(value)
    ? (value as HandoffCtaChannel)
    : null;
}

/** Clusters stored explicitly for one channel (settings UI). */
export function getEditableCtaClustersForChannel(
  all: CtaClusterRecord[] | null | undefined,
  channel: HandoffCtaChannel,
): CtaClusterRecord[] {
  return (all ?? [])
    .filter((cluster) => {
      const rowChannel = normalizeChannel(cluster.channel) ?? LEGACY_CHAT_CHANNEL;
      return rowChannel === channel;
    })
    .map((cluster) => ({
      ...cluster,
      qualificationQuestions: Array.isArray(cluster.qualificationQuestions)
        ? cluster.qualificationQuestions
        : [],
    }));
}

/** Runtime resolution with fallback: sms → phone → chat, phone → chat. */
export function resolveCtaClustersForChannel(
  all: CtaClusterRecord[] | null | undefined,
  channel: HandoffCtaChannel,
): CtaClusterRecord[] {
  const direct = getEditableCtaClustersForChannel(all, channel);
  if (direct.length > 0) {
    return direct;
  }

  if (channel === "sms") {
    const phone = getEditableCtaClustersForChannel(all, "phone");
    if (phone.length > 0) return phone;
  }

  if (channel === "phone" || channel === "sms" || channel === "talk" || channel === "email") {
    const chat = getEditableCtaClustersForChannel(all, "chat");
    if (chat.length > 0) return chat;
  }

  // Pre-migration rows without channel field (treated as chat-only storage).
  const legacy = (all ?? []).filter((cluster) => !normalizeChannel(cluster.channel));
  if (legacy.length > 0 && channel !== "chat") {
    return legacy.map((cluster) => ({
      ...cluster,
      qualificationQuestions: Array.isArray(cluster.qualificationQuestions)
        ? cluster.qualificationQuestions
        : [],
    }));
  }

  return [];
}

export function groupEditableCtaClustersByChannel(
  all: CtaClusterRecord[] | null | undefined,
): Record<HandoffCtaChannel, CtaClusterRecord[]> {
  return HANDOFF_CTA_CHANNELS.reduce(
    (acc, channel) => {
      acc[channel] = getEditableCtaClustersForChannel(all, channel);
      return acc;
    },
    {} as Record<HandoffCtaChannel, CtaClusterRecord[]>,
  );
}

export function handoffChannelFromResponsibilityId(
  channelId: string | undefined,
): HandoffCtaChannel {
  switch (channelId) {
    case "call":
      return "phone";
    case "sms":
      return "sms";
    case "email":
      return "email";
    case "talk":
      return "talk";
    case "aiChat":
    default:
      return "chat";
  }
}
